import { apiClient } from './api';

export interface ArtworkResponse {
  artwork_id: number;
  student_id?: number | null;
  student_name?: string;
  uploaded_by: number;
  uploaded_at: string;
  updated_at: string;
  tags: string[];
}

export interface ArtworkListResponse {
  artworks: ArtworkResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ArtworkFilters {
  student_id?: number;
  tags?: string[];
  uploaded_by?: number;
  uploaded_after?: string;
  uploaded_before?: string;
  search?: string;
}

export interface CreateArtworkRequest {
  student_id?: number;
  tag_ids?: number[];
}

export interface UpdateArtworkRequest {
  student_id?: number;
  tag_ids?: number[];
}

export interface BatchUploadRequest {
  student_id?: number;
  tag_ids?: number[];
}

export interface BatchUploadResult {
  success: number;
  failed: number;
  results: Array<{
    success: boolean;
    artwork?: ArtworkResponse;
    error?: string;
    filename?: string;
  }>;
}

export interface BatchUploadResponse {
  message: string;
  artworks: ArtworkResponse[];
  summary: {
    total: number;
    successful: number;
    failed: number;
  };
}

export interface ArtworkUploadProgress {
  fileIndex: number;
  filename: string;
  progress: number;
  status: 'pending' | 'uploading' | 'processing' | 'complete' | 'error';
  artwork?: ArtworkResponse;
  error?: string;
}

export class ArtworkService {
  // Remove the client-side rotated artworks tracking since we now use server-side versioning

  async getArtworks(
    page = 1,
    limit = 20,
    filters?: ArtworkFilters
  ): Promise<ArtworkListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters) {
      if (filters.student_id) {
        params.append('student_id', filters.student_id.toString());
      }
      if (filters.tags && filters.tags.length > 0) {
        params.append('tags', filters.tags.join(','));
      }
      if (filters.uploaded_by) {
        params.append('uploaded_by', filters.uploaded_by.toString());
      }
      if (filters.uploaded_after) {
        params.append('uploaded_after', filters.uploaded_after);
      }
      if (filters.uploaded_before) {
        params.append('uploaded_before', filters.uploaded_before);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
    }

    return apiClient.get<ArtworkListResponse>(`/artworks?${params}`);
  }

  async getAdminArtworks(
    page = 1,
    limit = 20,
    filters?: ArtworkFilters,
  ): Promise<ArtworkListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filters) {
      if (filters.student_id) params.append('student_id', filters.student_id.toString());
      if (filters.tags?.length) params.append('tags', filters.tags.join(','));
      if (filters.uploaded_by) params.append('uploaded_by', filters.uploaded_by.toString());
      if (filters.uploaded_after) params.append('uploaded_after', filters.uploaded_after);
      if (filters.uploaded_before) params.append('uploaded_before', filters.uploaded_before);
      if (filters.search) params.append('search', filters.search);
    }
    return apiClient.get<ArtworkListResponse>(`/artworks/admin?${params}`);
  }

  async getArtwork(id: number): Promise<ArtworkResponse> {
    return apiClient.get<ArtworkResponse>(`/artworks/${id}`);
  }

  async getAdminArtwork(id: number): Promise<ArtworkResponse> {
    return apiClient.get<ArtworkResponse>(`/artworks/admin/${id}`);
  }

  async uploadArtwork(
    file: File,
    metadata?: CreateArtworkRequest,
    onProgress?: (progress: number) => void
  ): Promise<ArtworkResponse> {
    return apiClient.uploadFile<ArtworkResponse>('/artworks/admin', file, {
      onProgress,
      additionalData: metadata,
    });
  }

  /**
   * Batch upload multiple artworks using the transactional batch endpoint
   * All uploads succeed or all fail - no partial uploads
   */
  async batchUploadArtworks(
    files: File[],
    metadata?: BatchUploadRequest,
    callbacks?: {
      onProgress?: (fileIndex: number, progress: number) => void;
      onFileComplete?: (fileIndex: number, artwork: ArtworkResponse) => void;
      onFileError?: (fileIndex: number, error: string, filename: string) => void;
    }
  ): Promise<BatchUploadResult> {
    const formData = new FormData();
    
    // Add all files to FormData
    files.forEach((file) => {
      formData.append('images', file);
    });
    
    // Apply one selected student to the whole batch. The server expands this
    // into per-file associations inside the transactional upload.
    if (metadata?.student_id !== undefined) {
      formData.append('student_id', String(metadata.student_id));
    }

    const response = await apiClient.post<{
      message: string;
      artworks: ArtworkResponse[];
      summary: {
        total: number;
        successful: number;
        failed: number;
      };
    }>('/artworks/admin/batch', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: callbacks?.onProgress ? (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          files.forEach((_, fileIndex) => callbacks.onProgress?.(fileIndex, percentCompleted));
        }
      } : undefined,
    });

    response.artworks.forEach((artwork, fileIndex) => {
      callbacks?.onFileComplete?.(fileIndex, artwork);
    });

    return {
      success: response.summary.successful,
      failed: response.summary.failed,
      results: response.artworks.map((artwork, index) => ({
        success: true,
        artwork,
        filename: files[index]?.name,
      })),
    };
  }

  async updateArtwork(id: number, updates: UpdateArtworkRequest): Promise<ArtworkResponse> {
    return apiClient.patch<ArtworkResponse>(`/artworks/admin/${id}`, updates);
  }

  async updateArtworkStudent(id: number, studentId: number | null): Promise<ArtworkResponse> {
    return apiClient.patch<ArtworkResponse>(`/artworks/admin/${id}`, { student_id: studentId });
  }

  async rotateArtwork(id: number, direction: 'clockwise' | 'counterclockwise' = 'clockwise'): Promise<void> {
    await apiClient.post(`/artworks/admin/${id}/rotate`, { direction });
  }

  async deleteArtwork(id: number): Promise<void> {
    await apiClient.delete(`/artworks/admin/${id}`);
  }

  async addTagsToArtwork(artworkId: number, tagNames: string[]): Promise<ArtworkResponse> {
    return apiClient.post<ArtworkResponse>(`/artworks/admin/${artworkId}/tags`, {
      tags: tagNames,
    });
  }

  async removeTagsFromArtwork(artworkId: number, tagNames: string[]): Promise<ArtworkResponse> {
    return apiClient.delete<ArtworkResponse>(`/artworks/admin/${artworkId}/tags`, {
      data: { tags: tagNames },
    });
  }

  async replaceArtworkTags(artworkId: number, tagNames: string[]): Promise<ArtworkResponse> {
    return apiClient.put<ArtworkResponse>(`/artworks/admin/${artworkId}/tags`, {
      tags: tagNames,
    });
  }

  // Image URL helpers with server-side versioning
  getImageUrl(artworkId: number, updatedAt?: string): string {
    return apiClient.getImageUrl(artworkId, 'image', updatedAt);
  }

  getThumbnailUrl(artworkId: number, updatedAt?: string): string {
    return apiClient.getImageUrl(artworkId, 'thumbnail', updatedAt);
  }

  // Convenience methods that accept an ArtworkResponse object
  getImageUrlFromArtwork(artwork: Pick<ArtworkResponse, 'artwork_id'> & Partial<Pick<ArtworkResponse, 'updated_at'>>): string {
    return this.getImageUrl(artwork.artwork_id, artwork.updated_at);
  }

  getThumbnailUrlFromArtwork(artwork: Pick<ArtworkResponse, 'artwork_id'> & Partial<Pick<ArtworkResponse, 'updated_at'>>): string {
    return this.getThumbnailUrl(artwork.artwork_id, artwork.updated_at);
  }

  async getPublicArtworkBlob(
    artworkId: number,
    variant: 'image' | 'thumbnail' = 'image',
    updatedAt?: string,
    signal?: AbortSignal,
  ): Promise<Blob> {
    const response = await fetch(apiClient.getImageUrl(artworkId, variant, updatedAt), {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      headers: { Accept: 'image/jpeg' },
      signal,
    });
    if (!response.ok) {
      throw new Error(`Artwork image request failed (${response.status})`);
    }
    return response.blob();
  }

  async getAdminArtworkBlob(
    artworkId: number,
    variant: 'image' | 'thumbnail' = 'image',
    signal?: AbortSignal,
  ): Promise<Blob> {
    return apiClient.getBlob(`/artworks/admin/${artworkId}/${variant}`, { signal });
  }

  // Search and filtering helpers
  async searchArtworks(query: string, limit = 10): Promise<ArtworkResponse[]> {
    const response = await this.getArtworks(1, limit, { search: query });
    return response.artworks;
  }

  async getArtworksByStudent(studentId: number, page = 1, limit = 20): Promise<ArtworkListResponse> {
    return this.getArtworks(page, limit, { student_id: studentId });
  }

  async getArtworksByTags(tags: string[], page = 1, limit = 20): Promise<ArtworkListResponse> {
    return this.getArtworks(page, limit, { tags });
  }

  async getRecentArtworks(limit = 10): Promise<ArtworkResponse[]> {
    const response = await this.getArtworks(1, limit);
    return response.artworks;
  }

  // Statistics
  async getUploadStats(): Promise<{
    total_artworks: number;
    uploads_this_month: number;
    uploads_this_week: number;
    uploads_today: number;
  }> {
    return apiClient.get('/artworks/admin/stats');
  }

  // Validation helpers
  validateImageFile(file: File): { valid: boolean; error?: string } {
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!validTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'File must be a valid image (JPEG, PNG, GIF, or WebP)',
      };
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'File size must be less than 10MB',
      };
    }

    return { valid: true };
  }

  validateImageFiles(files: File[]): {
    valid: File[];
    invalid: Array<{ file: File; error: string }>;
  } {
    const valid: File[] = [];
    const invalid: Array<{ file: File; error: string }> = [];

    files.forEach((file) => {
      const validation = this.validateImageFile(file);
      if (validation.valid) {
        valid.push(file);
      } else {
        invalid.push({ file, error: validation.error! });
      }
    });

    return { valid, invalid };
  }

  // File processing helpers
  async compressImage(file: File, maxWidth = 1920, quality = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          file.type,
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }
}

export const artworkService = new ArtworkService();
export default artworkService;
